import type { SaferGamblingQas } from '@content-utils/schemas'
import type { FaqSchemaItem } from '@nimbus/composables/useFaqSchema'
import { minimarkToHtml, type MinimarkOptions } from '@core/utils/minimark'

/** A Q&A as queried from `safer-gambling/qas`. */
export type SaferGamblingQaRecord = SaferGamblingQas & { stem?: string, body?: unknown }

export interface SaferGamblingQaItem {
    /** The file slug: the Q&A's identity for `?search=<slug>` and its `#<slug>` anchor. */
    id: string
    title: string
    /** Only questions get an anchor and enter the FAQPage schema. */
    isQuestion: boolean
    video_id?: string
    video_src?: string
    video_poster?: string
    body: unknown
    ctas: NonNullable<SaferGamblingQas['ctas']>
    expandableTextBlocks: NonNullable<SaferGamblingQas['expandableTextBlocks']>
    shortAnswer?: string
}

export function isSaferGamblingQuestion(title: string): boolean {
    return title.trim().endsWith('?')
}

export function mapSaferGamblingQas(records: SaferGamblingQaRecord[]): SaferGamblingQaItem[] {
    return records.map(qa => ({
        id: (qa.stem ?? '').split('/').pop() ?? '',
        title: qa.title,
        isQuestion: isSaferGamblingQuestion(qa.title),
        video_id: qa.video_id,
        video_src: qa.video_src,
        video_poster: qa.video_poster,
        body: qa.body,
        ctas: qa.ctas ?? [],
        expandableTextBlocks: qa.expandableTextBlocks ?? [],
        shortAnswer: qa.shortAnswer,
    }))
}

export function saferGamblingFaqItems(qas: SaferGamblingQaItem[], options: MinimarkOptions): FaqSchemaItem[] {
    return qas
        .filter(qa => qa.isQuestion)
        .map(qa => ({
            question: qa.title.trim(),
            answerHtml: minimarkToHtml(qa.body, options),
            routeHash: qa.id,
            shortAnswer: qa.shortAnswer,
        }))
}
